import Move from './move';
import Color from '@base/features/game/model/color';
import { t } from '@base/utils/helpers';
import { Subject } from 'rxjs';

export interface HistoryEntry {
  color: Color;
  dice1: number;
  dice2: number;
  moves: string;
}

export interface Current {
  color: Color;
  dice1: number;
  dice2: number;
  moves: Move[];
}

export interface IMovesHistory {
  history: HistoryEntry[],
  onMoveHistoryChangedEvent: Subject<Array<HistoryEntry>>;
  onCurrentTurnChanged: Subject<HistoryEntry>;
  setTurnChanged: (value: boolean) => void,
  addMove: (move: Move) => void,
  addRoll: (color: Color, dice1: number, dice2: number) => void,
  addCantMove: () => void,
  getLastMove: () => Move | null,
  undoLastMove: (move: Move) => Move,
  getMoves: () => Move[],
  clearHistory: () => void,
}

export default class MovesHistory implements IMovesHistory {
  history: HistoryEntry[] = [];
  current: Current | undefined;
  onMoveHistoryChangedEvent = new Subject<Array<HistoryEntry>>();
  onCurrentTurnChanged = new Subject<HistoryEntry>();
  turnChanged = true;


  setTurnChanged(value: boolean) {
    this.turnChanged = value;
  }

  addMove(move: Move) {
    if (!this.current) return;
    this.current.moves.push(move);
    this.emitCurrentTurnChanged();
  }

  addRoll(color: Color, dice1: number, dice2: number) {
    this.current && this.commitCurrentTurnToHistory();
    this.current = {color, dice1, dice2, moves: []};
    this.emitCurrentTurnChanged();
  }

  addCantMove() {
    if (!this.current) return;
    this.history.push({...this.current, moves: t('can_t_move')});
    this.current = undefined;
    this.emitHistoryChanged();
    this.emitCurrentTurnChanged();
  }

  getLastMove() {
    if (!this.current) throw new Error('No current move')
    return this.current.moves[this.current.moves.length - 1];
  }

  undoLastMove(move: Move) {
    if (!this.current) throw new Error('No current turn. Nothing to undo');
    for (let i = this.current.moves.length - 1; i >= 0; i--) {
      if (this.current.moves[i].equat(Move.reverseMove(move))) {
        const undoMove = this.current.moves[i];
        this.current.moves.splice(i, 1);
        this.emitCurrentTurnChanged();
        return undoMove;
      }
    }
    throw new Error('Can`t find move in undo`s to remove');
  }

  getMoves() {
    if (!this.current) throw new Error('No current turn. No moves to return');
    return this.current.moves;
  }

  clearHistory() {
    this.history = [];
    this.current = undefined;
    this.turnChanged = false;
    this.emitHistoryChanged();
  }

  private commitCurrentTurnToHistory() {
    this.turnChanged = false;
    const currentHistoryEntry = this.currentToHistoryEntry();
    // Когда мы заходим на стол и ходы уже сделаны, то у нас есть только информация о кубиках
    // Но нет информации о ходах. Поэтому первый ход мы игнорируем и не вносим в историю
    if (!this.history.length && !currentHistoryEntry.moves) return;
    if (!currentHistoryEntry.moves.length) currentHistoryEntry.moves = t('can_t_move');
    this.history.push(currentHistoryEntry); 
    this.emitHistoryChanged();
  }

  private emitHistoryChanged() {
    this.onMoveHistoryChangedEvent.next([...this.history]);
  }

  private emitCurrentTurnChanged() {
    if (!this.current) throw new Error('No current turn. Nothing to emit');
    this.onCurrentTurnChanged.next(this.currentToHistoryEntry());
  }

  private currentToHistoryEntry() {
    if (!this.current) throw new Error('No current. Cannot convert to history string');
    const moves = this.current.moves.reduce((acc, cur) => acc += cur.toHistory() + ' ', '');
    const entry: HistoryEntry = {...this.current, moves};
    return entry;
  }
}
